'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CountryCode } from '@sola/schema/dotcom/shared/geo.ts';
export const postalCodePatternUS = /^\d{5}((-|(\s*))?\d{4})?$/;
export const postalCodeMaskMX = /^\d{5}$/;
export const postalCodePatternCanada = /^[a-zA-Z]\d[a-zA-Z] ?\d[a-zA-Z]\d$/;
const datePatterns = {
  usAndEs: /^\d{2}\/\d{2}\/\d{4}$/,
  ko: /^\d{4}\.\d{2}\.\d{2}$/
};
export type GeneratePostalPatternFunc = (countryCode: CountryCode) => {
  value: RegExp;
  message: string;
} | null;

/**
 * Hook for generating postal code patterns based on country ISO code.
 */
export const usePostalPattern = (countryCode: CountryCode) => {
  const {
    t
  } = useTranslation(['forms']);
  const [generatePattern, setGeneratePattern] = useState<GeneratePostalPatternFunc | null>(null);

  /**
   * Function to lazy load the pattern assets.
   * This will only be called once, and serves to load pattern dependencies that are needed
   * for the generatePattern function, which can then be subsequently used to dynamically generate
   * a patterns, placeholders, and validations based on country code.
   *
   * @TODO Don't forget to check how these imports are cached to make sure browser isn't downloading a unique bundle every time.
   *
   */
  const loadPattern = useCallback(async () => {
    const generatePatternFunc = (countryCodeToPattern: CountryCode) => {
      switch (countryCodeToPattern) {
        case 'US':
          return {
            value: postalCodePatternUS,
            message: t('forms:errors.invalidZipCode')
          };
        case 'MX':
          return {
            value: postalCodeMaskMX,
            message: t('forms:errors.invalidPostalCodeMexico')
          };
        case 'CA':
          return {
            value: postalCodePatternCanada,
            message: t('forms:errors.invalidPostalCodeCanada')
          };
        default:
          return null;
      }
    };
    setGeneratePattern(() => generatePatternFunc);
  }, []);

  /**
   * Generate a pattern anytime the country code changes.
   */
  const pattern = useMemo(() => {
    if (!generatePattern) {
      return;
    }
    return generatePattern(countryCode);
  }, [countryCode, generatePattern]);
  return {
    loadPattern,
    pattern
  };
};
type GenerateDatePatternFunc = (localeCode: string) => RegExp | null;

/**
 * Hook for generating date patterns based on locale.
 */
export const useDatePattern = localeCode => {
  const $ = _c(6);
  useTranslation();
  const [generatePattern, setGeneratePattern] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = async () => {
      const generatePatternFunc = _temp;
      setGeneratePattern(() => generatePatternFunc);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const loadPattern = t0;
  let t1;
  bb0: {
    if (!generatePattern) {
      t1 = undefined;
      break bb0;
    }
    let t2;
    if ($[1] !== generatePattern || $[2] !== localeCode) {
      t2 = generatePattern(localeCode);
      $[1] = generatePattern;
      $[2] = localeCode;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    t1 = t2;
  }
  const pattern = t1;
  let t2;
  if ($[4] !== pattern) {
    t2 = {
      loadPattern,
      pattern
    };
    $[4] = pattern;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export const removeIllegalChars = (event: React.ChangeEvent<HTMLInputElement>, regex: RegExp) => {
  const matches = (event.currentTarget.value.match(regex) || []).length;
  if (matches > 0) {
    let start = 0;
    let end = 0;
    if (event.currentTarget.type !== 'number') {
      start = event.currentTarget.selectionStart ? event.currentTarget.selectionStart - 2 * matches : event.currentTarget.value.length;
      end = event.currentTarget.selectionEnd ? event.currentTarget.selectionEnd - 2 * matches : event.currentTarget.value.length;
    }
    event.currentTarget.value = event.currentTarget.value.replace(regex, '');
    if (event.currentTarget.type !== 'number') {
      // Reset cursor position
      event.currentTarget.selectionStart = start;
      event.currentTarget.selectionEnd = end;
    }
  }
};
function _temp(localeCodeToPattern) {
  switch (localeCodeToPattern) {
    case "ko":
      {
        return datePatterns.ko;
      }
    default:
      {
        return datePatterns.usAndEs;
      }
  }
}